import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon, LeafIcon } from 'lucide-react';
import { TreatmentList } from './TreatmentList';
import { Faq } from '../shared/Faq';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { PageHero } from '../shared/PageHero';
import { Reveal } from '../shared/Reveal';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { salonFaq } from '../../data/faq';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { usePages, useSite } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { listNl } from '../../core/format/text';
import { mediaOr } from '../../core/rules/media';
import { shopLabels } from '../../data/shopLabels';
import { btnOnClay, btnPrimaryLg, btnSecondaryLg, chipCls } from '../../ui/styles';
import { closingHeading, quietLink, sectionHeading } from '../../ui/sections';
const categories = ['Anti-ageing', 'Huidverbetering', 'Ontspanning', 'Losse applicaties'] as const;
type Filter = (typeof categories)[number] | 'Alles';
const filters: readonly Filter[] = ['Alles', ...categories];

/**
 * De salonpagina: hier valt de beslissing om te boeken. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wie behandelt me en kan ik boeken (hero, sand) · wat kies ik (alle
 * behandelingen, shell) · waarom werkt het (bindweefselmassage, klei) · hoe houd ik het vast
 * (van salon naar thuis, sand) · praktisch (vragen, shell) · twijfel (blush).
 */
export function Treatments() {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const {
    treatments,
    brandNames
  } = useCatalog();
  const [active, setActive] = useState<Filter>('Alles');
  const copy = pages.treatments;
  const {
    bindweefsel
  } = copy;
  // De losse bindweefselmassage: een losse applicatie met bindweefselmassage (in het beheer aan te vinken).
  const bindweefselLos = treatments.find(t => t.bindweefsel && t.category === 'Losse applicaties');
  const heroMedia = mediaOr(copy.hero.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een gezichtsbehandeling`
  });
  const massageMedia = mediaOr(bindweefsel.media, {
    image: founder.atWork,
    alt: ''
  });
  const allGroups = categories.map(c => ({
    category: c,
    items: treatments.filter(t => t.category === c)
  })).filter(g => g.items.length > 0);
  // Een categorie zonder behandelingen (alles offline gezet) is geen filter: die gaf een lege pagina.
  const visibleFilters = filters.filter(f => f === 'Alles' || allGroups.some(g => g.category === f));
  const groups = allGroups.filter(g => active === 'Alles' || g.category === active);
  return <>
      <PageHero titleId="salon-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8510" data-mp="wpa2yoti2s7n" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              {fill(copy.hero.treatedBy)} <span className="text-ink" data-id="element-8511" data-mp="2887ezvecg5rs">{founder.name}</span>
            </>,
      sub: `Alleen op afspraak · ${policyCopy.experience} ervaring`
    }} actions={(copy.hero.cta || copy.hero.secondaryCta) && <>
              {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8512" data-mp="2vzmihrhuwt4m">
                  {fill(copy.hero.cta)}
                </button>}
              {copy.hero.secondaryCta && <a href="#behandelingen" className={btnSecondaryLg} data-id="element-8513" data-mp="2m6nv2cy3j8ml">
                  {fill(copy.hero.secondaryCta)}
                </a>}
            </>}
    // De agenda zit vaak vol: geef wie moet wachten meteen iets te doen.
    footnote={<p data-id="element-8514" data-mp="2ygda5w7gw8h4">
            <span className="text-ink" data-id="element-8515" data-mp="2yk13ovcwwuvu">{fill(copy.hero.agenda.title)}</span> {fill(copy.hero.agenda.body)}{' '}
            {copy.hero.agenda.cta && <Link to="/routines" className="whitespace-nowrap text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8516" data-mp="2pi0802xbugwa">
                {fill(copy.hero.agenda.cta)}
              </Link>}
          </p>} data-id="element-8509" data-mp="ru4hts7x9hkm" />

      {/* Alle behandelingen, direct na de hero: wie hier komt, wil kiezen. */}
      <section id="behandelingen" className="scroll-mt-24 bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="behandelingen-title" data-id="element-8517" data-mp="3m9ljj5drrc0w">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8518" data-mp="3denq7x6plx91">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-8519" data-mp="3dd2tq20u448j">
            <h2 id="behandelingen-title" className={`${sectionHeading} text-ink lg:col-span-6`} data-id="element-8520" data-mp="4xsz1ez9y1as">
              Alle behandelingen
            </h2>
            {copy.includes.length > 0 && <div className="lg:col-span-6" data-id="element-8521" data-mp="j3l3cajw5xl7">
                <p className="text-[14px] text-ink" data-id="element-8522" data-mp="f3amu3yh3os3">{fill(copy.hero.includesTitle)}</p>
                <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5" data-id="element-8523" data-mp="f1hqpilhkmyc">
                  {copy.includes.map(item => <li key={item} className="flex items-center gap-1.5 text-[14px] text-muted" data-id="element-8524" data-mp="nvw6rnduc1ii">
                      <CheckIcon className="h-3.5 w-3.5 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8525" data-mp="5rz94xj7hm8" />
                      {item}
                    </li>)}
                </ul>
              </div>}
          </div>

          {/* Op de telefoon een schuifstrip: de categorieën blijven op één regel. */}
          {visibleFilters.length > 2 && <div className="-mx-6 mt-10 flex gap-2 overflow-x-auto px-6 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden" role="group" aria-label="Toon categorie" data-id="element-8526" data-mp="57hfcyzm2lh6">
              {visibleFilters.map(c => <button key={c} type="button" aria-pressed={active === c} onClick={() => setActive(c)} className={`${chipCls(active === c)} shrink-0`} data-id="element-8527" data-mp="1onc1298br5v3">
                  {c}
                </button>)}
            </div>}

          <div className="mt-12 space-y-16 lg:space-y-20" data-id="element-8528" data-mp="22l0o9npv6p3b">
            {groups.map(group => <div key={group.category} className="lg:grid lg:grid-cols-12 lg:gap-16" data-id="element-8529" data-mp="14xrwxc36mlte">
                <h3 className="text-[14px] text-clay lg:col-span-3 lg:pt-8" data-id="element-8530" data-mp="19vjyyyxjjobb">{group.category}</h3>
                <div className="mt-3 lg:col-span-9 lg:mt-0" data-id="element-8531" data-mp="1euo4nfl9b3ww">
                  <TreatmentList items={group.items} data-id="element-8532" data-mp="1et3cfwmezx27" />
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bindweefselmassage: waarom het werkt, het ene verhaal op klei */}
      <section className="bg-clay text-shell" aria-labelledby="bindweefsel" data-id="element-8533" data-mp="r5c0wv8p7o1z">
        <div className="grid lg:grid-cols-12" data-id="element-8534" data-mp="r390dh9x6cj0">
          <Reveal className="lg:order-2 lg:col-span-5" data-id="element-8535" data-mp="jl1fau7fwwtm">
            <MediaView media={massageMedia} sizes="(min-width: 1024px) 42vw, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/10] lg:aspect-auto lg:h-full lg:min-h-[680px]" data-id="element-8536" data-mp="emzvg726wth3" />
          </Reveal>

          <div className="px-6 py-20 sm:px-10 lg:order-1 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-8537" data-mp="4otvhuzy1xev">
            <p className="text-[14px] text-shell" data-id="element-8538" data-mp="o07z2qeykmi">{bindweefsel.title}</p>
            <h2 id="bindweefsel" className={`mt-5 max-w-xl ${sectionHeading}`} data-id="element-8539" data-mp="3s1cmzr9ktwbq">
              {fill(bindweefsel.claim)}
            </h2>
            <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-shell" data-id="element-8540" data-mp="3n3b3x6c5hx09">{fill(bindweefsel.intro)}</p>

            <div className="mt-12 grid gap-10 sm:grid-cols-2 sm:gap-12" data-id="element-8541" data-mp="3i5jcsejbyex4">
              <BulletList title="Voor wie is het goed?" items={bindweefsel.forWho} data-id="element-8542" data-mp="3d3bw5bnk2aog" />
              <BulletList title="Wat doet het?" items={bindweefsel.effects} data-id="element-8543" data-mp="392s0kcimrf4f" />
            </div>

            {bindweefsel.rhythm && <p className="mt-8 text-[15px] text-shell" data-id="element-8544" data-mp="10vn5pc3hsl3l">{fill(bindweefsel.rhythm)}</p>}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6" data-id="element-8545" data-mp="15sdmy2r19mql">
              {bindweefsel.cta && <button type="button" onClick={() => openBooking(bindweefselLos?.slug)} aria-haspopup="dialog" className={btnOnClay} data-id="element-8546" data-mp="qy8tjoolqih3">
                  {fill(bindweefsel.cta)}
                </button>}
              {bindweefsel.secondaryCta && <Link to="/webshop" className={`${quietLink} text-shell`} data-id="element-8547" data-mp="mvcfmba0bajh">
                  {fill(bindweefsel.secondaryCta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8548" data-mp="83jypuukdofn" />
                </Link>}
            </div>
          </div>
        </div>
      </section>

      {/* Van salon naar thuis: de brug naar de webshop */}
      <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="salon-bridge" data-id="element-8549" data-mp="36tj18mo8uur">
        <div className="mx-auto grid max-w-content items-center gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8550" data-mp="3gn5ipvmxfkpb">
          <Reveal className="lg:col-span-5" data-id="element-8551" data-mp="3bpm58xy1zbyf">
            <MediaView media={{
            image: founder.withProducts,
            alt: `${founder.name} met de producten waar ze mee werkt`,
            focus: {
              x: 50,
              y: 35
            }
          }} sizes="(min-width: 1024px) 40vw, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/10] lg:aspect-[4/5]" data-id="element-8552" data-mp="3bqndkunopkcg" />
          </Reveal>
          <div className="lg:col-span-7" data-id="element-8553" data-mp="3unolan8mhyk3">
            <h2 id="salon-bridge" className={`${sectionHeading} text-ink`} data-id="element-8554" data-mp="3umx3cf7y7zca">
              <HeadingText heading={copy.bridge.heading} accentBlock data-id="element-8555" data-mp="c9p948j2wd6m" />
            </h2>
            {copy.bridge.body && <p className="mt-7 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-8556" data-mp="88wx57itnu6n">{fill(copy.bridge.body)}</p>}
            {brandNames.length > 0 && <p className="mt-7 flex max-w-xl items-start gap-3 border-t border-ink/15 pt-6 text-[15px] leading-relaxed text-muted" data-id="element-8557" data-mp="m5ahcoeqq9ot">
                <LeafIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={1.5} aria-hidden="true" data-id="element-8558" data-mp="1vhpi3oj6turt" />
                100% vegan: {listNl(brandNames)}.
              </p>}
            {/* Knoppen zonder tekst (leeg gelaten in Paginateksten) niet tonen, zoals op de andere pagina's. */}
            {(copy.bridge.cta || copy.bridge.secondaryCta) && <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-8559" data-mp="2egbif16vrhuo">
                {copy.bridge.cta && <Link to="/routines" className={btnPrimaryLg} data-id="element-8560" data-mp="2aegl07s2lom6">
                    {fill(copy.bridge.cta)}
                  </Link>}
                {copy.bridge.secondaryCta && <Link to="/webshop" className={btnSecondaryLg} data-id="element-8561" data-mp="20hbxa3iktl39">
                    {fill(copy.bridge.secondaryCta)}
                  </Link>}
              </div>}
          </div>
        </div>
      </section>

      {/* Praktisch */}
      <section className="bg-shell py-20 sm:py-24 lg:py-32" data-id="element-8562" data-mp="p3q7qh8t1hh7">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8563" data-mp="eyrmo1qoqbtc">
          <Faq items={salonFaq} title={fill(copy.faqTitle)} data-id="element-8564" data-mp="jw094eiv920e" />
        </div>
      </section>

      {/* Afsluiter: voor wie nog twijfelt */}
      <section className="bg-blush py-20 sm:py-24" aria-labelledby="salon-closing" data-id="element-8565" data-mp="ou1rsycdqzbc">
        <div className="mx-auto grid max-w-content gap-8 px-6 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10" data-id="element-8566" data-mp="tnedazlripzv">
          <div className="lg:col-span-7" data-id="element-8567" data-mp="xneswnkll78i">
            <h2 id="salon-closing" className={`${closingHeading} text-ink`} data-id="element-8568" data-mp="12n14ksks2ff7">
              <HeadingText heading={copy.closing.heading} data-id="element-8569" data-mp="12p46oaz7fz0a" />
            </h2>
            <p className="mt-4 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-8570" data-mp="17mnjd6g983q4">
              {copy.closing.body && `${fill(copy.closing.body)} `}
              {policyCopy.consultCredit}.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end" data-id="element-8571" data-mp="39fk4jm3vomny">
            {copy.closing.cta && <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8572" data-mp="36bmdxwo77u8i">
                {fill(copy.closing.cta)}
              </button>}
            {copy.closing.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnSecondaryLg} data-id="element-8573" data-mp="3gbmy1ktw6edr">
                {fill(copy.closing.secondaryCta)}
                <span className="sr-only" data-id="element-8574" data-mp="3pa8ftdrjhpdh">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>
        </div>
      </section>
    </>;
}
function BulletList({
  title,
  items
}: {
  title: string;
  items: string[];
}) {
  if (items.length === 0) return null;
  return <div data-id="element-8575" data-mp="3u1gog7l1p6pl">
      <h3 className="font-display text-2xl font-light" data-id="element-8576" data-mp="3u1qj48h1c21m">{title}</h3>
      <ul className="mt-5 divide-y divide-shell/25 border-y border-shell/25" data-id="element-8577" data-mp="3u3jolkfwwg81">
        {items.map(item => <li key={item} className="py-4 text-[15px] leading-snug text-shell" data-id="element-8578" data-mp="bthleyj8e60q">
            {item}
          </li>)}
      </ul>
    </div>;
}